import {
    Button,
    ConfigProvider,
    Divider,
    Drawer,
    Form,
    Grid,
    Input,
    message,
    Modal
} from 'antd'
import { addDays, differenceInDays } from 'date-fns'
import { enUS, ru } from 'date-fns/locale'
import React, { useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useTranslation } from 'react-i18next'
import api from '../../../../shared/api/api'
import cls from './HeroBooking.module.scss'

const DEPOSIT = 0

const getDailyRate = (days: number): number => {
    if (days >= 8) return 60
    if (days >= 4) return 80
    return 90
}

const bookingTheme = {
    token: {
        borderRadius: 10,
        colorPrimary: '#E97316',
        fontFamily: 'Giltroy, -apple-system, BlinkMacSystemFont, sans-serif'
    },
    components: {
        Button: {
            controlHeight: 40,
            borderRadius: 999
        }
    }
} as const

interface HeroBookingProps {
    open: boolean
    onClose: () => void
}

const HeroBooking: React.FC<HeroBookingProps> = ({ open, onClose }) => {
    const { t, i18n } = useTranslation()
    const screens = Grid.useBreakpoint()
    const useDrawer = screens.sm === false || screens.sm === undefined

    const currentLocale = i18n.language === 'ru' ? ru : enUS

    const [form] = Form.useForm()
    const [range, setRange] = useState([
        {
            startDate: new Date(),
            endDate: addDays(new Date(), 2),
            key: 'selection'
        }
    ])
    const [rangeChanged, setRangeChanged] = useState(false)

    const tripDays = useMemo(
        () => differenceInDays(range[0].endDate, range[0].startDate),
        [range]
    )
    const dailyRate = useMemo(() => getDailyRate(tripDays), [tripDays])
    const totalPrice = useMemo(() => tripDays * dailyRate + DEPOSIT, [tripDays, dailyRate])

    useEffect(() => {
        if (!open) {
            form.resetFields()
            setRangeChanged(false)
        }
    }, [open, form])

    const handleCancel = () => {
        onClose()
        form.resetFields()
    }

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()
            const start = range[0].startDate
            const end = range[0].endDate

            const days = differenceInDays(end!, start!)

            if (days < 3) {
                message.warning(t('hero.min3Days'))
                return
            }

            const totalPrice = days * getDailyRate(days) + DEPOSIT

            const payload = {
                startDate: start.toLocaleDateString('ru-RU'),
                endDate: end.toLocaleDateString('ru-RU'),
                contact: values.contact,
                comment: values.comment,
                price: totalPrice
            }

            await api.post('/vanlife', payload)
            message.success(t('hero.successMessage'))
            onClose()
            form.resetFields()
        } catch (error) {
            console.error(error)
            message.error(t('hero.errorMessage'))
        }
    }

    const titleNode = (
        <div className={cls.sheetHeader}>
            <p className={cls.sheetTitle}>{t('hero.modalTitle')}</p>
            <p className={cls.sheetSub}>{t('hero.bookingSubtitle')}</p>
        </div>
    )

    const footerNode = (
        <div className={cls.footer}>
            <Button type="text" className={cls.footerGhost} onClick={handleCancel}>
                {t('hero.cancel')}
            </Button>
            <Button type="primary" onClick={handleSubmit}>
                {t('hero.submit')}
            </Button>
        </div>
    )

    const modalFrameStyles = {
        content: {
            padding: 0,
            borderRadius: 12,
            overflow: 'hidden' as const
        },
        header: {
            margin: 0,
            padding: '18px 20px 8px',
            borderBottom: 'none'
        },
        body: { padding: '0 20px 4px' },
        footer: {
            margin: 0,
            padding: '12px 20px 18px',
            borderTop: '1px solid rgba(0, 0, 0, 0.06)'
        }
    }

    const drawerFrameStyles = {
        header: {
            padding: '18px 20px 8px',
            borderBottom: 'none'
        },
        body: {
            padding: '4px 20px 8px',
            paddingBottom: 8
        },
        footer: {
            padding: '12px 20px',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
            borderTop: '1px solid rgba(0, 0, 0, 0.06)'
        }
    }

    const formNode = (
        <Form
            layout="vertical"
            form={form}
            size="middle"
            requiredMark={false}
            colon={false}
            className={cls.form}
        >
            <div className={cls.calendarWrap}>
                <Form.Item label={t('hero.dateRange')} style={{ marginBottom: 0 }}>
                    <DateRange
                        className={cls.dateRange}
                        showDateDisplay={false}
                        locale={currentLocale}
                        editableDateInputs={false}
                        months={1}
                        onChange={(item: any) => {
                            setRange([item.selection])
                            setRangeChanged(true)
                        }}
                        scroll={{
                            enabled: true
                        }}
                        moveRangeOnFirstSelection={false}
                        ranges={range}
                        minDate={new Date()}
                    />
                </Form.Item>
                {rangeChanged && tripDays < 3 && (
                    <div className={cls.minDaysWarning}>
                        {t('hero.min3Days')}
                    </div>
                )}
                {tripDays >= 3 && (
                    <div className={cls.priceCard}>
                        <span className={cls.priceLabel}>
                            {t('hero.daysHint', { count: tripDays })} × ${dailyRate}
                        </span>
                        <span className={cls.priceTotal}>
                            ${totalPrice}
                        </span>
                    </div>
                )}
            </div>

            <Divider className={cls.divider} />

            <Form.Item
                name="contact"
                label={t('hero.contact')}
                rules={[
                    {
                        required: true,
                        message: t('hero.contactRequired')
                    }
                ]}
            >
                <Input placeholder={t('hero.contactPlaceholder')} allowClear />
            </Form.Item>

            <Form.Item name="comment" label={t('hero.comment')}>
                <Input.TextArea
                    rows={2}
                    placeholder={t('hero.commentPlaceholder')}
                    autoSize={{ minRows: 2, maxRows: 4 }}
                />
            </Form.Item>
        </Form>
    )

    return (
        <ConfigProvider theme={bookingTheme}>
            {useDrawer ? (
                <Drawer
                    title={titleNode}
                    placement="bottom"
                    height="85vh"
                    open={open}
                    onClose={handleCancel}
                    destroyOnClose
                    closable
                    styles={drawerFrameStyles}
                    footer={footerNode}
                >
                    {formNode}
                </Drawer>
            ) : (
                <Modal
                    title={titleNode}
                    centered
                    open={open}
                    onCancel={handleCancel}
                    destroyOnClose
                    width={380}
                    footer={footerNode}
                    styles={modalFrameStyles}
                >
                    {formNode}
                </Modal>
            )}
        </ConfigProvider>
    )
}

export default HeroBooking
